import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";
import { useCart } from "../context/CartContext";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiSun,
  FiMoon,
  FiArrowUp,
  FiArrowLeft,
  FiCheckCircle,
  FiMenu,
  FiX,
  FiSearch,
  FiShoppingCart,
  FiClock,
  FiTruck,
  FiMail,
} from "react-icons/fi";

import { AiFillHeart } from "react-icons/ai";
import PropTypes from "prop-types";
import ProfileModal from "../components/ProfileModal";

// Restaurant Data (excluding Taste of Punjab)
const restaurants = [
  {
    id: 1,
    name: "I Love Pizza",
    category: "Pizza, Italian",
    rating: 4.8,
    image: "/ilovepizzapic.jpg",
    estimatedTime: "20-30 min",
    popularItems: [
      { id: "m101", name: "Margherita Pizza", price: 155, img: "https://source.unsplash.com/300x200/?margherita" },
      { id: "m1", name: "French Fries (Large)", price: 95, img: "https://source.unsplash.com/300x200/?fries" },
    ],
    discount: "10% off orders above ₹300",
  },
  {
    id: 2,
    name: "Sharma Fast Food",
    category: "Street Food",
    rating: 4.5,
    image: "/2023-10-18.webp",
    estimatedTime: "15-25 min",
    popularItems: [
      { id: "d5", name: "Paneer Roll", price: 156, img: "https://source.unsplash.com/300x200/?paneer-roll" },
      { id: "d6", name: "Chow Mein", price: 120, img: "https://source.unsplash.com/300x200/?chowmein" },
    ],
    discount: "Free delivery on orders above ₹200",
  },
  {
    id: 3,
    name: "Coffee Express",
    category: "Café, Beverages",
    rating: 4.3,
    image: "/492123435_1148793163932964_7238524074888884240_n.jpeg",
    estimatedTime: "10-20 min",
    popularItems: [
      { id: "d9", name: "Cold Coffee", price: 144, img: "https://source.unsplash.com/300x200/?cold-coffee" },
      { id: "d11", name: "Chocolate Muffin", price: 114, img: "https://source.unsplash.com/300x200/?muffin" },
    ],
  },
  {
    id: 5,
    name: "Moonlight Cafe",
    category: "Café",
    rating: 4.4,
    image: "/moonlight.jpg",
    estimatedTime: "15-25 min",
    popularItems: [
      { id: "s1", name: "Strawberry Shake", price: 84, img: "/StrawberryMilkshake.webp" },
      { id: "p1", name: "Single Cheese Pizza", price: 120, img: "https://source.unsplash.com/300x200/?cheese" },
    ],
  },
];

// Custom debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

// Toast Component
const Toast = ({ message, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0, x: 100 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 100 }}
      className="fixed top-4 right-4 bg-[#00b86b] text-black px-4 py-3 rounded-lg z-[1000] flex items-center gap-2 text-sm font-semibold"
    >
      <FiCheckCircle className="w-5 h-5" />
      {message}
    </motion.div>
  );
};

Toast.propTypes = {
  message: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

// RestaurantCard Component
const RestaurantCard = React.memo(function RestaurantCard({
  restaurant,
  mode,
  darkMode,
  toggleFavourite,
  isFavourite,
  addToCart,
  decreaseQuantity,
}) {
  const { cart } = useCart();
  const [toastMessage, setToastMessage] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  return (
    <>
      {toastMessage && <Toast message={toastMessage} onClose={() => setToastMessage(null)} />}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className={`rounded-lg shadow-lg hover:shadow-[#00b86b]/20 transition-all duration-300 ${darkMode ? "bg-[#222] text-white" : "bg-white text-black"}`}
      >
        <Link to={`/menu/${restaurant.id}?mode=${mode}`} aria-label={`View menu for ${restaurant.name}`}>
          <div className="relative w-full h-48">
            {!imageLoaded && (
              <div className={`absolute inset-0 ${darkMode ? "bg-[#333]" : "bg-[#e6f0ff]"} animate-pulse rounded-t-lg`} />
            )}
            <img
              src={restaurant.image}
              alt={restaurant.name}
              className={`w-full h-full object-cover rounded-t-lg ${imageLoaded ? "opacity-100" : "opacity-0"}`}
              loading="lazy"
              onLoad={() => setImageLoaded(true)}
              onError={(e) => {
                e.target.src = "/zoomo-mascot.png";
                setImageLoaded(true);
              }}
            />
            {restaurant.discount && (
              <span className="absolute top-2 left-2 bg-red-600 text-white text-xs px-2 py-1 rounded-lg shadow-sm">
                {restaurant.discount}
              </span>
            )}
          </div>
        </Link>
        <motion.button
          onClick={() => {
            toggleFavourite(restaurant.id);
            setToastMessage(`${restaurant.name} ${isFavourite ? "removed from" : "added to"} favorites!`);
          }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          className="absolute top-4 right-4 text-2xl focus:outline-none focus:ring-2 focus:ring-[#00b86b]"
          aria-label={isFavourite ? `Remove ${restaurant.name} from favorites` : `Add ${restaurant.name} to favorites`}
        >
          <motion.span animate={{ scale: isFavourite ? 1.2 : 1 }} transition={{ duration: 0.2 }}>
            {isFavourite ? "♥" : "♡"}
          </motion.span>
        </motion.button>
        <div className="p-4">
          <Link to={`/menu/${restaurant.id}?mode=${mode}`}>
            <h2 className="text-lg font-semibold mb-1">{restaurant.name}</h2>
            <p className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-600"}`}>
              {restaurant.category} • {restaurant.estimatedTime}
            </p>
            <p className="text-yellow-500 text-sm mt-1">★ {restaurant.rating.toFixed(1)}</p>
          </Link>
          <div className="mt-3 space-y-2">
            {restaurant.popularItems.map((item) => {
              const cartItem = cart.find((cartItem) => cartItem.id === item.id);
              const quantity = cartItem ? cartItem.quantity : 0;

              return (
                <div key={item.id} className="flex justify-between items-center text-sm">
                  <span className="truncate">{item.name}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-[#00b86b] font-semibold">₹{item.price.toFixed(2)}</span>
                    <div className="flex items-center gap-1">
                      {quantity > 0 && (
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => {
                            decreaseQuantity(item.id);
                            setToastMessage(`${item.name} quantity decreased!`);
                          }}
                          className="bg-red-600 hover:bg-red-700 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-red-400"
                          aria-label={`Decrease quantity of ${item.name}`}
                        >
                          -
                        </motion.button>
                      )}
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => {
                          addToCart({ id: item.id, name: item.name, price: item.price, img: item.img, restaurantId: restaurant.id }, 1);
                          setToastMessage(`${item.name} ${quantity > 0 ? "quantity increased" : "added to"} cart!`);
                        }}
                        className="bg-[#00b86b] hover:bg-[#009e5c] text-black px-3 py-1 rounded-lg text-xs font-semibold transition-all"
                        aria-label={`Add ${item.name} to cart`}
                      >
                        + {quantity > 0 ? `${quantity}` : "Add"}
                      </motion.button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </motion.div>
    </>
  );
});

RestaurantCard.propTypes = {
  restaurant: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    category: PropTypes.string.isRequired,
    rating: PropTypes.number.isRequired,
    image: PropTypes.string.isRequired,
    estimatedTime: PropTypes.string.isRequired,
    popularItems: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        price: PropTypes.number.isRequired,
        img: PropTypes.string.isRequired,
      })
    ).isRequired,
    discount: PropTypes.string,
  }).isRequired,
  mode: PropTypes.string.isRequired,
  darkMode: PropTypes.bool.isRequired,
  toggleFavourite: PropTypes.func.isRequired,
  isFavourite: PropTypes.bool.isRequired,
  addToCart: PropTypes.func.isRequired,
  decreaseQuantity: PropTypes.func.isRequired,
};

const RestaurantCardSkeleton = ({ darkMode }) => (
  <div className={`rounded-lg shadow-lg ${darkMode ? "bg-[#222]" : "bg-white"} h-64 animate-pulse`}>
    <div className={`w-full h-48 ${darkMode ? "bg-[#333]" : "bg-gray-200"} rounded-t-lg`} />
    <div className="p-4 space-y-2">
      <div className={`h-4 ${darkMode ? "bg-[#333]" : "bg-gray-200"} rounded-full w-3/4`} />
      <div className={`h-3 ${darkMode ? "bg-[#333]" : "bg-gray-200"} rounded-full w-1/2`} />
    </div>
  </div>
);

const RestaurantsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { darkMode, toggleDarkMode } = useTheme();
  const {
    cart,
    addToCart,
    decreaseQuantity,
    toggleFavouriteRestaurant,
    toggleFavouriteDish,
    favourites = { restaurants: [], dishes: [] },
    recommendedDishes = [],
    rewardPoints = 0,
    pastOrders = [],
  } = useCart();

  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const mode = query.get("mode") || "delivery";

  const [showQuiz, setShowQuiz] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({ mood: "", cuisine: "" });
  const [quizResult, setQuizResult] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [sortOption, setSortOption] = useState("rating");
  const [isLoading, setIsLoading] = useState(true);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [toastMessage, setToastMessage] = useState("");
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const cartTotalItems = useMemo(() => cart.reduce((sum, item) => sum + item.quantity, 0), [cart]);

  const filteredRestaurants = useMemo(() => {
    let result = restaurants.filter(
      (rest) =>
        rest.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
        rest.category.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
    );
    if (categoryFilter) {
      result = result.filter((rest) => rest.category.includes(categoryFilter));
    }
    return result.sort((a, b) =>
      sortOption === "rating" ? b.rating - a.rating : a.name.localeCompare(b.name)
    );
  }, [debouncedSearchTerm, categoryFilter, sortOption]);

  const personalizedRecommendations = useMemo(() => {
    if (!recommendedDishes.length) return [];
    const favouriteCategories = favourites.dishes
      .map((id) => recommendedDishes.find((d) => d.id === id)?.category)
      .filter(Boolean);
    return recommendedDishes.filter(
      (dish) => favouriteCategories.includes(dish.category) || dish.isPopular
    );
  }, [favourites.dishes, recommendedDishes]);

  useEffect(() => {
    setTimeout(() => setIsLoading(false), 1000);
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (quizResult) {
      localStorage.setItem("quizResult", JSON.stringify(quizResult));
    }
  }, [quizResult]);

  const handleModeChange = useCallback(
    (newMode) => {
      query.set("mode", newMode);
      navigate(`/restaurants?${query.toString()}`);
    },
    [navigate, query]
  );

  const handleQuizSubmit = useCallback(() => {
    const { mood, cuisine } = quizAnswers;
    let recommendedRestaurant = null;
    if (mood === "Quick" && cuisine === "Street Food") {
      recommendedRestaurant = restaurants.find((r) => r.name === "Sharma Fast Food");
    } else if (mood === "Relax" && cuisine === "Italian") {
      recommendedRestaurant = restaurants.find((r) => r.name === "I Love Pizza");
    } else if (mood === "Chill" && cuisine === "Beverages") {
      recommendedRestaurant = restaurants.find((r) => r.name === "Coffee Express");
    } else if (mood === "Casual" && cuisine === "Cafe") {
      recommendedRestaurant = restaurants.find((r) => r.name === "Moonlight Cafe");
    } else {
      recommendedRestaurant = restaurants[0];
    }
    setQuizResult(recommendedRestaurant);
    setRecentlyViewed((prev) => [
      recommendedRestaurant,
      ...prev.filter((r) => r.id !== recommendedRestaurant.id),
    ].slice(0, 3));
    setShowQuiz(false);
    setToastMessage(`We recommend ${recommendedRestaurant.name}!`);
  }, [quizAnswers]);

  const clearFilters = useCallback(() => {
    setSearchTerm("");
    setCategoryFilter("");
    setSortOption("rating");
    setToastMessage("Filters cleared.");
  }, []);

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    if (newsletterEmail.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      setToastMessage("Subscribed to newsletter!");
      setNewsletterEmail("");
    } else {
      setToastMessage("Invalid email address.");
    }
  };

  const scrollToTop = useCallback(() => window.scrollTo({ top: 0, behavior: "smooth" }), []);

  const isQuizValid = quizAnswers.mood && quizAnswers.cuisine;

  // Load user avatar from localStorage
  const savedProfile = JSON.parse(localStorage.getItem("profile") || "{}");
  const userAvatar = savedProfile.avatar || "/zoomo-mascot.png";

  return (
    <div className={`min-h-screen flex flex-col font-uber ${darkMode ? "bg-[#111] text-white" : "bg-[#fafafa] text-black"} relative overflow-x-hidden transition-colors duration-300`}>
      <style jsx global>{`
        @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap');
        .font-uber { font-family: 'Montserrat', sans-serif; }
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: ${darkMode ? '#333' : '#ffffff'}; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #00b86b; border-radius: 10px; }
        .animate-slideIn { animation: slideIn 0.5s ease-in-out; }
        .animate-fadeIn { animation: fadeIn 0.5s ease-out; }
        @keyframes slideIn { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        button:focus, input:focus { outline: none; box-shadow: 0 0 0 3px rgba(0, 184, 107, 0.3); }
        .text-black { color: ${darkMode ? '#ffffff' : '#000000'} !important; }
        .bg-[#e6f0ff] { background-color: ${darkMode ? '#222' : '#e6f0ff'} !important; }
        .border-gray-300 { border-color: ${darkMode ? '#666' : '#d1d5db'} !important; }
        .text-gray-600 { color: ${darkMode ? '#aaaaaa' : '#4b5563'} !important; }
        @media (max-width: 768px) {
          .grid-cols-3 { grid-template-columns: 1fr; }
          .grid-cols-4 { grid-template-columns: 1fr 1fr; }
          .px-6 { padding-left: 1rem; padding-right: 1rem; }
        }
        @media (max-width: 640px) {
          .grid-cols-2 { grid-template-columns: 1fr; }
          .text-3xl { font-size: 1.5rem; }
          .text-2xl { font-size: 1.25rem; }
          .px-4 { padding-left: 0.75rem; padding-right: 0.75rem; }
        }
      `}</style>
      {toastMessage && <Toast message={toastMessage} onClose={() => setToastMessage(null)} />}
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
        className={`fixed top-0 left-0 w-full py-3 px-4 sm:px-6 flex justify-between items-center z-50 ${darkMode ? "bg-[#111]/95" : "bg-[#fafafa]/95"} backdrop-blur-md transition-all duration-300 ${isScrolled ? "shadow-sm" : "shadow-none"}`}
        role="banner"
      >
        <div className="flex items-center gap-3">
          <motion.img
            src="/zoomo-logo.png"
            alt="Zoomo Eats Logo"
            className="w-10 h-10"
            whileHover={{ scale: 1.1 }}
            transition={{ duration: 0.3 }}
            onError={(e) => (e.target.src = "/zoomo-mascot.png")}
          />
          <h2 className="text-xl sm:text-2xl font-extrabold tracking-tight uppercase">
            ZOOMO <span className="text-[#00b86b]">EATS</span>
          </h2>
        </div>
        <nav className="hidden md:flex items-center gap-4" role="navigation">
          <motion.button
            onClick={toggleDarkMode}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="p-2 rounded-lg bg-[#00b86b] hover:bg-[#009e5c] text-black text-lg transition-all duration-300"
            aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
          >
            {darkMode ? <FiSun className="w-6 h-6" /> : <FiMoon className="w-6 h-6" />}
          </motion.button>
          <motion.button
            onClick={() => setProfileOpen(true)}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="relative group"
            aria-label="Open user profile modal"
          >
            <img
              src={userAvatar}
              alt="User Avatar"
              className="w-10 h-10 rounded-full object-cover border-2 border-[#00b86b] group-hover:scale-110 transition-transform duration-300"
              onError={(e) => (e.target.src = "/zoomo-mascot.png")}
            />
          </motion.button>
          <Link to="/cart" className="relative" aria-label="View cart">
            <FiShoppingCart className="w-8 h-8 text-[#00b86b]" />
            {cartTotalItems > 0 && (
              <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
                {cartTotalItems}
              </span>
            )}
          </Link>
        </nav>
        <motion.button
          className="md:hidden text-[#00b86b]"
          onClick={() => setIsMenuOpen((prev) => !prev)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          aria-label={isMenuOpen ? "Close menu" : "Open menu"}
        >
          {isMenuOpen ? <FiX className="w-8 h-8" /> : <FiMenu className="w-8 h-8" />}
        </motion.button>
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, x: -100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -100 }}
              transition={{ duration: 0.3 }}
              className={`fixed top-0 left-0 w-full h-full ${darkMode ? "bg-[#111]" : "bg-[#fafafa]"} md:hidden flex flex-col items-center justify-center z-40`}
            >
              <motion.button
                onClick={() => setIsMenuOpen(false)}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className="absolute top-4 right-4 text-[#00b86b]"
                aria-label="Close menu"
              >
                <FiX className="w-10 h-10" />
              </motion.button>
              <div className="flex flex-col items-center gap-8">
                <motion.button
                  onClick={() => {
                    setProfileOpen(true);
                    setIsMenuOpen(false);
                  }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="text-2xl font-semibold text-[#00b86b] py-4 px-8 rounded-lg hover:bg-[#00b86b]/10 transition-all"
                  aria-label="Open user profile"
                >
                  Profile
                </motion.button>
                <Link
                  to="/cart"
                  onClick={() => setIsMenuOpen(false)}
                  className="text-2xl font-semibold text-[#00b86b] py-4 px-8 rounded-lg hover:bg-[#00b86b]/10 transition-all"
                  aria-label="View cart"
                >
                  Cart
                </Link>
                <motion.button
                  onClick={toggleDarkMode}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="text-2xl font-semibold text-[#00b86b] py-4 px-8 rounded-lg hover:bg-[#00b86b]/10 transition-all flex items-center gap-2"
                  aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
                >
                  {darkMode ? <FiSun className="w-6 h-6" /> : <FiMoon className="w-6 h-6" />}
                  {darkMode ? "Light Mode" : "Dark Mode"}
                </motion.button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>

      <main className="pt-16 px-4 pb-16 max-w-7xl mx-auto" role="main">
        <section className="flex flex-col items-center mb-12 animate-slideIn">
          <h1 className="text-3xl md:text-4xl font-extrabold text-center mb-4 text-[#00b86b]">
            Restaurants for <span className="text-[#00b86b]">{mode.charAt(0).toUpperCase() + mode.slice(1)}</span>
          </h1>
          <nav className="flex flex-wrap justify-center gap-3 mb-6" role="navigation">
            {["delivery", "pickup", "dine-in"].map((opt) => (
              <motion.button
                key={opt}
                onClick={() => handleModeChange(opt)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-300 shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#00b86b] ${
                  opt === mode
                    ? "bg-[#00b86b] text-black"
                    : darkMode
                    ? "bg-[#333] text-white hover:bg-[#444]"
                    : "bg-[#e6f0ff] text-black hover:bg-[#d3e3ff]"
                }`}
                aria-label={`Select ${opt} mode`}
              >
                {opt.charAt(0).toUpperCase() + opt.slice(1)}
              </motion.button>
            ))}
          </nav>
          <div className="flex flex-col sm:flex-row items-center gap-3 w-full max-w-3xl">
            <div className="relative flex-1">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search restaurants or cuisines..."
                className={`w-full px-4 py-3 rounded-lg bg-[#e6f0ff] text-black placeholder:text-gray-500 border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00b86b] text-sm pl-10 shadow-sm transition-all duration-300 ${
                  darkMode ? "bg-[#333] border-gray-700 text-white placeholder:text-gray-400" : ""
                }`}
                aria-label="Search restaurants"
              />
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            </div>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className={`px-4 py-3 rounded-lg bg-[#e6f0ff] text-black placeholder:text-gray-500 border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00b86b] text-sm shadow-sm transition-all duration-300 ${
                darkMode ? "bg-[#333] border-gray-700 text-white" : ""
              }`}
              aria-label="Filter by category"
            >
              <option value="">All Categories</option>
              <option value="Italian">Italian</option>
              <option value="Café">Café</option>
              <option value="Street Food">Street Food</option>
            </select>
            <select
              value={sortOption}
              onChange={(e) => setSortOption(e.target.value)}
              className={`px-4 py-3 rounded-lg bg-[#e6f0ff] text-black placeholder:text-gray-500 border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00b86b] text-sm shadow-sm transition-all duration-300 ${
                darkMode ? "bg-[#333] border-gray-700 text-white" : ""
              }`}
              aria-label="Sort restaurants"
            >
              <option value="rating">Sort by Rating</option>
              <option value="name">Sort by Name</option>
            </select>
            <motion.button
              onClick={clearFilters}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`px-4 py-3 rounded-lg text-sm font-semibold transition-all duration-300 shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#00b86b] ${
                darkMode ? "bg-[#333] text-white hover:bg-[#444]" : "bg-[#e6f0ff] text-black hover:bg-[#d3e3ff]"
              }`}
              aria-label="Clear all filters"
            >
              Clear Filters
            </motion.button>
          </div>
        </section>

        {pastOrders.length > 0 && (
          <section className="mb-12 animate-slideIn">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-4 text-[#00b86b]">
              Track Your <span className="text-[#00b86b]">Order</span>
            </h2>
            <div className={`p-4 rounded-lg shadow-lg ${darkMode ? "bg-[#222] text-white" : "bg-[#fafafa] text-black"}`}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-sm font-semibold">Order #{pastOrders[0].id}</p>
                  <p className={`text-xs ${darkMode ? "text-gray-400" : "text-gray-600"}`}>Estimated Delivery: 15 mins</p>
                </div>
                <motion.button
                  onClick={() => navigate("/track-order")}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="bg-[#00b86b] hover:bg-[#009e5c] text-black px-3 py-2 rounded-lg text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-[#00b86b] transition-all shadow-sm hover:shadow-md"
                  aria-label="Track order"
                >
                  Track Now
                </motion.button>
              </div>
              <div className="flex justify-between items-center">
                <div className="text-center">
                  <FiCheckCircle className="text-[#00b86b] text-xl" />
                  <p className="text-xs mt-1">Order Placed</p>
                </div>
                <div className="flex-1 h-1 bg-gray-300 dark:bg-gray-700 mx-2">
                  <div className="h-full bg-[#00b86b]" style={{ width: "50%" }} />
                </div>
                <div className="text-center">
                  <FiClock className="text-gray-400 text-xl" />
                  <p className="text-xs mt-1">Preparing</p>
                </div>
                <div className="flex-1 h-1 bg-gray-300 dark:bg-gray-700 mx-2" />
                <div className="text-center">
                  <FiTruck className="text-gray-400 text-xl" />
                  <p className="text-xs mt-1">On the Way</p>
                </div>
              </div>
            </div>
          </section>
        )}

        <section className="text-center mb-12 animate-slideIn">
          <motion.button
            onClick={() => setShowQuiz(true)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="bg-[#00b86b] hover:bg-[#009e5c] text-black px-6 py-3 rounded-lg text-base font-semibold focus:outline-none focus:ring-2 focus:ring-[#00b86b] transition-all shadow-sm hover:shadow-md"
            aria-label="Take food mood quiz"
          >
            Find Your Food Mood 🍕
          </motion.button>
        </section>

        {showQuiz && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 animate-slideIn"
            role="dialog"
            aria-modal="true"
            aria-labelledby="quiz-modal-title"
          >
            <div className={`p-4 rounded-lg shadow-lg max-w-md w-full mx-2 ${darkMode ? "bg-[#111] text-white" : "bg-[#fafafa] text-black"}`}>
              <h2 id="quiz-modal-title" className="text-lg font-semibold mb-3">
                Food Mood Quiz
              </h2>
              <div className="space-y-3 text-sm">
                <div>
                  <label htmlFor="mood-select" className="block text-sm mb-1">
                    What's your mood?
                  </label>
                  <select
                    id="mood-select"
                    value={quizAnswers.mood}
                    onChange={(e) => setQuizAnswers({ ...quizAnswers, mood: e.target.value })}
                    className={`w-full px-3 py-2 rounded-lg bg-[#e6f0ff] text-black placeholder:text-gray-500 border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00b86b] text-sm ${
                      darkMode ? "bg-[#333] border-gray-700 text-white" : ""
                    }`}
                    aria-label="Select mood"
                  >
                    <option value="">Select...</option>
                    <option value="Quick">Quick Bite</option>
                    <option value="Relax">Relax & Dine</option>
                    <option value="Chill">Chill & Sip</option>
                    <option value="Casual">Casual Hangout</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="cuisine-select" className="block text-sm mb-1">
                    Preferred Cuisine?
                  </label>
                  <select
                    id="cuisine-select"
                    value={quizAnswers.cuisine}
                    onChange={(e) => setQuizAnswers({ ...quizAnswers, cuisine: e.target.value })}
                    className={`w-full px-3 py-2 rounded-lg bg-[#e6f0ff] text-black placeholder:text-gray-500 border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00b86b] text-sm ${
                      darkMode ? "bg-[#333] border-gray-700 text-white" : ""
                    }`}
                    aria-label="Select cuisine"
                  >
                    <option value="">Select...</option>
                    <option value="Italian">Italian</option>
                    <option value="Street">Street Food</option>
                    <option value="Beverages">Beverages</option>
                    <option value="Cafe">Cafe</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-3">
                <motion.button
                  onClick={() => setShowQuiz(false)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`text-sm ${darkMode ? "text-gray-400 hover:text-gray-200" : "text-gray-500 hover:text-gray-700"} focus:outline-none`}
                  aria-label="Cancel quiz"
                >
                  Cancel
                </motion.button>
                <motion.button
                  onClick={handleQuizSubmit}
                  disabled={!isQuizValid}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`px-3 py-2 rounded-lg text-sm font-semibold focus:outline-none transition-all shadow-sm hover:shadow-md ${
                    isQuizValid ? "bg-[#00b86b] hover:bg-[#009e5c] text-black" : "bg-gray-400 text-gray-200 cursor-not-allowed"
                  }`}
                  aria-label="Submit quiz"
                >
                  Submit
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}

        {quizResult && (
          <section className="mb-12 animate-slideIn">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-4 text-[#00b86b]">
              We Recommend: <span className="text-[#00b86b]">{quizResult.name}</span>
            </h2>
            <RestaurantCard
              restaurant={quizResult}
              mode={mode}
              darkMode={darkMode}
              toggleFavourite={toggleFavouriteRestaurant}
              isFavourite={favourites.restaurants.includes(quizResult.id)}
              addToCart={addToCart}
              decreaseQuantity={decreaseQuantity}
            />
          </section>
        )}

        {recentlyViewed.length > 0 && (
          <section className="mb-12 animate-slideIn">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-4 text-[#00b86b]">
              Recently <span className="text-[#00b86b]">Viewed</span>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {recentlyViewed.map((rest) => (
                <RestaurantCard
                  key={rest.id}
                  restaurant={rest}
                  mode={mode}
                  darkMode={darkMode}
                  toggleFavourite={toggleFavouriteRestaurant}
                  isFavourite={favourites.restaurants.includes(rest.id)}
                  addToCart={addToCart}
                  decreaseQuantity={decreaseQuantity}
                />
              ))}
            </div>
          </section>
        )}

        {personalizedRecommendations.length > 0 && (
          <section className="mb-12 animate-slideIn">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-4 text-[#00b86b]">
              Just for <span className="text-[#00b86b]">You</span>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {personalizedRecommendations.map((dish) => {
                const cartItem = cart.find((item) => item.id === dish.id);
                const quantity = cartItem ? cartItem.quantity : 0;

                return (
                  <motion.div
                    key={dish.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className={`rounded-lg shadow-lg hover:shadow-[#00b86b]/20 transition-all duration-300 ${
                      darkMode ? "bg-[#222] text-white" : "bg-[#fafafa] text-black"
                    }`}
                  >
                    <div className="p-3">
                      <h3 className="text-base font-semibold mb-1 truncate">{dish.name}</h3>
                      <p className={`text-xs ${darkMode ? "text-gray-400" : "text-gray-600"} mb-1`}>
                        {dish.restaurant} • {dish.category}
                      </p>
                      <p className="text-sm font-semibold mb-2 text-[#00b86b]">₹{dish.price.toFixed(2)}</p>
                      <div className="flex items-center gap-2">
                        {quantity > 0 && (
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => {
                              decreaseQuantity(dish.id);
                              setToastMessage(`${dish.name} quantity decreased!`);
                            }}
                            className="bg-red-600 hover:bg-red-700 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-red-400"
                            aria-label={`Decrease quantity of ${dish.name}`}
                          >
                            -
                          </motion.button>
                        )}
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => {
                            addToCart({ id: dish.id, name: dish.name, price: dish.price, img: dish.img, restaurantId: dish.restaurantId }, 1);
                            setToastMessage(`${dish.name} ${quantity > 0 ? "quantity increased in" : "added to"} cart!`);
                          }}
                          className="bg-[#00b86b] hover:bg-[#009e5c] text-black px-3 py-2 rounded-lg text-sm font-semibold transition-all focus:outline-none focus:ring-2 focus:ring-[#00b86b]"
                          aria-label={`Add ${dish.name} to cart`}
                        >
                          + {quantity > 0 ? `${quantity}` : "Add"}
                        </motion.button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </section>
        )}

        <section className="animate-slideIn">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-6 text-[#00b86b]">
            All <span className="text-[#00b86b]">Restaurants</span>
          </h2>
          {isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {Array(6)
                .fill(0)
                .map((_, i) => (
                  <RestaurantCardSkeleton key={i} darkMode={darkMode} />
                ))}
            </div>
          ) : filteredRestaurants.length === 0 ? (
            <div className="text-center py-8">
              <p className={`text-base ${darkMode ? "text-gray-400" : "text-gray-600"}`}>
                No restaurants found. Try adjusting your search or filters.
              </p>
              <motion.button
                onClick={clearFilters}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="mt-4 bg-[#00b86b] hover:bg-[#009e5c] text-black px-4 py-2 rounded-lg text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-[#00b86b] transition-all shadow-sm hover:shadow-md"
                aria-label="Clear all filters"
              >
                Clear Filters
              </motion.button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {filteredRestaurants.map((rest) => (
                <RestaurantCard
                  key={rest.id}
                  restaurant={rest}
                  mode={mode}
                  darkMode={darkMode}
                  toggleFavourite={toggleFavouriteRestaurant}
                  isFavourite={favourites.restaurants.includes(rest.id)}
                  addToCart={addToCart}
                  decreaseQuantity={decreaseQuantity}
                />
              ))}
            </div>
          )}
        </section>
      </main>

      <div className="fixed bottom-4 right-4 z-[1000] flex flex-col gap-2">
        <Link to="/landing" aria-label="Go back to landing page">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            className="bg-[#00b86b] hover:bg-[#009e5c] text-black p-3 rounded-full shadow-lg transition-all focus:outline-none focus:ring-2 focus:ring-[#00b86b]"
          >
            <FiArrowLeft className="w-5 h-5" />
          </motion.button>
        </Link>
        <Link to="/cart" aria-label="View cart">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            className="bg-[#00b86b] hover:bg-[#009e5c] text-black p-3 rounded-full shadow-lg relative transition-all focus:outline-none focus:ring-2 focus:ring-[#00b86b]"
          >
            <FiShoppingCart className="w-5 h-5" />
            {cartTotalItems > 0 && (
              <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
                {cartTotalItems}
              </span>
            )}
          </motion.button>
        </Link>
        {showBackToTop && (
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={scrollToTop}
            className="bg-[#00b86b] hover:bg-[#009e5c] text-black p-3 rounded-full shadow-lg transition-all focus:outline-none focus:ring-2 focus:ring-[#00b86b]"
            aria-label="Scroll to top"
          >
            <FiArrowUp className="w-5 h-5" />
          </motion.button>
        )}
      </div>

      <ProfileModal
        darkMode={darkMode}
        setToastMessage={setToastMessage}
        toggleFavouriteDish={toggleFavouriteDish}
        favourites={favourites}
        isOpen={profileOpen}
        onClose={() => setProfileOpen(false)}
      />

      {/* Footer */}
      <motion.footer
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className={`py-12 px-4 md:px-6 ${darkMode ? "bg-[#111] text-white" : "bg-[#fafafa] text-black"} animate-slideIn`}
        role="contentinfo"
      >
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h4 className="text-lg font-semibold mb-4 text-[#00b86b]">About Zoomo Eats</h4>
              <p className="text-base font-medium">
                Zoomo Eats delivers your favorite meals from top restaurants in Jourian with speed and ease.
              </p>
              <div className="flex gap-4 mt-4">
                {["facebook", "twitter", "instagram"].map((platform, i) => (
                  <motion.a
                    key={platform}
                    href={`https://${platform}.com`}
                    target="_blank"
                    rel="noopener noreferrer"
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ delay: i * 0.1, duration: 0.3 }}
                    whileHover={{ scale: 1.2 }}
                    className="text-[#00b86b] hover:text-[#009e5c] text-lg"
                    aria-label={`Visit our ${platform} page`}
                  >
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path
                        d={
                          platform === "facebook"
                            ? "M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H7v-3h3V9.5C10 7.57 11.57 6 13.5 6H16v3h-2c-.55 0-1 .45-1 1v2h3v3h-3v6.95c5.05-.5 9-4.76 9-9.95z"
                            : platform === "twitter"
                            ? "M22.46 6c-.77.35-1.6.58-2.46.69a4.3 4.3 0 001.88-2.38c-.83.5-1.75.85-2.72 1.05a4.28 4.28 0 00-7.28 3.9A12.15 12.15 0 013.15 4.78a4.27 4.27 0 001.32 5.71c-.69-.02-1.34-.21-1.9-.5v.05a4.28 4.28 0 003.43 4.19c-.63.17-1.29.26-1.97.26-.48 0-.95-.05-1.41-.15a4.28 4.28 0 003.99 2.97A8.58 8.58 0 012 18.62a12.08 12.08 0 006.55 1.92c7.86 0 12.15-6.51 12.15-12.15 0-.18 0-.37-.01-.55A8.7 8.7 0 0022.46 6z"
                            : "M12 2.04c-5.5 0-9.96 4.46-9.96 9.96 0 4.4 3.58 8.04 8.12 8.94v-6.32h-2.44v-2.62h2.44v-1.99c0-2.41 1.47-3.72 3.61-3.72 1.03 0 1.91.08 2.17.11v2.51h-1.49c-1.17 0-1.39 .56-1.39 1.37v1.79h2.78l-.36 2.62h-2.42v6.32c4.54-.9 8.12-4.54 8.12-8.94 0-5.5-4.46-9.96-9.96-9.96z"
                        }
                      />
                    </svg>
                  </motion.a>
                ))}
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              <h4 className="text-lg font-semibold mb-4 text-[#00b86b]">Quick Links</h4>
              <ul className="text-base font-medium space-y-2">
                <li>
                  <Link
                    to="/faq"
                    className="hover:text-[#00b86b] focus:outline-none focus:ring-2 focus:ring-[#00b86b] transition-colors"
                    aria-label="View FAQ"
                  >
                    FAQ
                  </Link>
                </li>
                <li>
                  <Link
                    to="/contact"
                    className="hover:text-[#00b86b] focus:outline-none focus:ring-2 focus:ring-[#00b86b] transition-colors"
                    aria-label="Contact us"
                  >
                    Contact
                  </Link>
                </li>
                <li>
                  <Link
                    to="/terms"
                    className="hover:text-[#00b86b] focus:outline-none focus:ring-2 focus:ring-[#00b86b] transition-colors"
                    aria-label="View Terms of Service"
                  >
                    Terms
                  </Link>
                </li>
                <li>
                  <Link
                    to="/privacy"
                    className="hover:text-[#00b86b] focus:outline-none focus:ring-2 focus:ring-[#00b86b] transition-colors"
                    aria-label="View Privacy Policy"
                  >
                    Privacy
                  </Link>
                </li>
              </ul>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4, duration: 0.5 }}
            >
              <h4 className="text-lg font-semibold mb-4 text-[#00b86b]">Stay Connected</h4>
              <p className="text-base font-medium mb-4">Join our newsletter for exclusive deals!</p>
              <form onSubmit={handleNewsletterSubmit} className="flex flex-col gap-3">
                <input
                  type="email"
                  placeholder="Enter email"
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  className={`w-full px-4 py-2 rounded-lg bg-[#e6f0ff] text-black border-gray-300 focus:ring-2 focus:ring-[#00b86b] text-base transition-all duration-300 ${darkMode ? "bg-[#222] text-white border-gray-700" : ""}`}
                  aria-label="Newsletter email"
                />
                <motion.button
                  type="submit"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="bg-[#00b86b] hover:bg-[#009e5c] text-black px-4 py-2 rounded-lg text-base font-semibold transition-all flex items-center justify-center gap-2"
                  aria-label="Subscribe to newsletter"
                >
                  <FiMail className="w-5 h-5" /> Subscribe
                </motion.button>
              </form>
            </motion.div>
          </div>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.6, duration: 0.5 }}
            className="text-center text-base font-medium"
          >
            <p className="flex items-center justify-center gap-1 flex-wrap">
              © {new Date().getFullYear()} <span className="font-semibold text-[#00b86b]">Zoomo Eats</span> — Designed with
              <span className="text-green-500 text-xl">
                <AiFillHeart />
              </span>
              by Shiv
            </p>
          </motion.div>
        </div>
      </motion.footer>
    </div>
  );
};

export default RestaurantsPage;